import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, CheckCircle2, Loader2, AlertTriangle, Clock, Trash2, X, Plus, Users, Upload, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Expiration status config
const EXP_STATUS = {
  current:       { label: "Submitted",          cls: "bg-emerald-100 text-emerald-700" },
  expiring_soon: { label: "Expiring Soon",       cls: "bg-amber-100 text-amber-700" },
  expired:       { label: "Update & Re-sign",    cls: "bg-red-100 text-red-700" },
  not_started:   { label: "Not Started",         cls: "bg-slate-100 text-slate-500" },
};

// Health & medical records status config — matches enrollment form badge style
const DOC_STATUS = {
  current:      { label: "On File",         cls: "bg-emerald-100 text-emerald-700" },
  expires_soon: { label: "Expiring Soon",   cls: "bg-amber-100 text-amber-700" },
  expired:      { label: "Update Required", cls: "bg-red-100 text-red-700" },
  needs_upload: { label: "Needs Upload",    cls: "bg-slate-100 text-slate-500" },
};

const HEALTH_DOC_TYPES = ["health_records", "shot_records"];
const HEALTH_DOC_LABELS = {
  health_records: "Child Health Report",
  shot_records:   "Child Shot Records",
};

function daysUntil(isoDate) {
  if (!isoDate) return null;
  return Math.ceil((new Date(isoDate) - new Date()) / (1000 * 60 * 60 * 24));
}

function computeExpStatus(sub) {
  if (!sub?.expiresAt) return "current";
  const days = daysUntil(sub.expiresAt);
  if (days <= 0) return "expired";
  if (days <= 30) return "expiring_soon";
  return "current";
}

// Canvas signature pad
function SignaturePad({ value, onChange, required }) {
  const canvasRef = { current: null };
  const drawing = { current: false };

  const start = (e) => {
    drawing.current = true;
    const rect = e.target.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    const ctx = e.target.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    const rect = e.target.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    const ctx = e.target.getContext("2d");
    ctx.lineWidth = 2; ctx.strokeStyle = "#1e293b";
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = (e) => {
    drawing.current = false;
    onChange(e.target.toDataURL());
  };
  const clear = (e) => {
    const canvas = e.target.closest(".sig-wrapper").querySelector("canvas");
    canvas.getContext("2d").clearRect(0, 0, 400, 120);
    onChange("");
  };

  return (
    <div className="sig-wrapper space-y-1">
      <canvas width={400} height={120}
        className="border-2 border-dashed border-slate-300 rounded-xl w-full touch-none bg-white cursor-crosshair"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
        style={{ maxWidth: "100%" }}
      />
      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-400">Sign using finger or mouse</p>
        <button type="button" onClick={clear} className="text-xs text-red-400 hover:text-red-600">Clear</button>
      </div>
      {value && <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Signature captured</p>}
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  if (field.type === "section-heading")
    return <h3 className="text-base font-bold text-slate-800 border-b border-slate-200 pb-2 mt-4">{field.label}</h3>;
  if (field.type === "instruction")
    return <p className="text-sm text-slate-600 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 italic">{field.label}</p>;

  const shared = "w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400";
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-700">
        {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {field.helpText && <p className="text-xs text-slate-400">{field.helpText}</p>}
      {field.type === "text"     && <input type="text"   value={value||""} onChange={e=>onChange(e.target.value)} placeholder={field.placeholder} className={shared} />}
      {field.type === "email"    && <input type="email"  value={value||""} onChange={e=>onChange(e.target.value)} placeholder={field.placeholder} className={shared} />}
      {field.type === "phone"    && <input type="tel"    value={value||""} onChange={e=>onChange(e.target.value)} placeholder={field.placeholder||"(555) 123-4567"} className={shared} />}
      {field.type === "number"   && <input type="number" value={value||""} onChange={e=>onChange(e.target.value)} placeholder={field.placeholder} className={shared} />}
      {field.type === "date"     && <input type="date"   value={value||""} onChange={e=>onChange(e.target.value)} className={shared} />}
      {field.type === "textarea" && <textarea value={value||""} onChange={e=>onChange(e.target.value)} placeholder={field.placeholder} rows={3} className={shared} />}
      {field.type === "dropdown" && (
        <select value={value||""} onChange={e=>onChange(e.target.value)} className={shared}>
          <option value="">Select...</option>
          {(field.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {field.type === "radio" && (
        <div className="space-y-2">
          {(field.options||[]).map(o=>(
            <label key={o} className={`flex items-center gap-2 cursor-pointer rounded-xl border px-3 py-2 transition ${value===o?"border-emerald-400 bg-emerald-50":"border-slate-200"}`}>
              <input type="radio" checked={value===o} onChange={()=>onChange(o)} className="accent-emerald-600"/>
              <span className="text-sm text-slate-700">{o}</span>
            </label>
          ))}
        </div>
      )}
      {field.type === "checkbox" && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!value} onChange={e=>onChange(e.target.checked)} className="accent-emerald-600 h-4 w-4"/>
          <span className="text-sm text-slate-700">{field.placeholder||"Yes"}</span>
        </label>
      )}
      {field.type === "checkbox-group" && (
        <div className="space-y-2">
          {(field.options||[]).map(o=>(
            <label key={o} className={`flex items-center gap-2 cursor-pointer rounded-xl border px-3 py-2 transition ${(value||[]).includes(o)?"border-emerald-400 bg-emerald-50":"border-slate-200"}`}>
              <input type="checkbox" checked={(value||[]).includes(o)}
                onChange={e=>{const cur=value||[];onChange(e.target.checked?[...cur,o]:cur.filter(v=>v!==o));}}
                className="accent-emerald-600"/>
              <span className="text-sm text-slate-700">{o}</span>
            </label>
          ))}
        </div>
      )}
      {field.type === "signature" && <SignaturePad value={value} onChange={onChange} required={field.required} />}
      {field.type === "file" && (
        <input type="file" onChange={e=>onChange(e.target.files[0])}
          className="w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-semibold hover:file:bg-emerald-100"/>
      )}
    </div>
  );
}

// Groups flat field array into sections by section-heading dividers
function groupFieldsIntoSections(fields) {
  const sections = [];
  let current = { heading: null, fields: [] };
  for (const f of fields) {
    if (f.type === "section-heading") {
      if (current.heading || current.fields.length) sections.push({ ...current });
      current = { heading: f, fields: [] };
    } else if (f.type === "instruction") {
      current.fields.push(f);
    } else {
      current.fields.push(f);
    }
  }
  if (current.heading || current.fields.length) sections.push(current);
  return sections;
}

// Detect if two consecutive sections should render side-by-side (Mother/Father etc.)
const SIDE_BY_SIDE_PAIRS = [
  [/mother|mom|parent\s*1|guardian\s*1/i, /father|dad|parent\s*2|guardian\s*2/i],
  [/parent\s*\/?\s*guardian\s*1/i,        /parent\s*\/?\s*guardian\s*2/i],
];
function isSideBySidePair(s1, s2) {
  if (!s1?.heading || !s2?.heading) return false;
  const l1 = s1.heading.label, l2 = s2.heading.label;
  return SIDE_BY_SIDE_PAIRS.some(([p1, p2]) => p1.test(l1) && p2.test(l2));
}

function SectionCard({ section, responses, onChange }) {
  // Group consecutive halfWidth fields into 2-column pairs
  const rows = [];
  let i = 0;
  while (i < section.fields.length) {
    const f = section.fields[i];
    const next = section.fields[i + 1];
    if (f.halfWidth && next?.halfWidth) {
      rows.push(
        <div key={`pair-${f.id}`} className="grid grid-cols-2 gap-3">
          <FieldRenderer field={f} value={responses[f.id]} onChange={v => onChange(f.id, v)} />
          <FieldRenderer field={next} value={responses[next.id]} onChange={v => onChange(next.id, v)} />
        </div>
      );
      i += 2;
    } else {
      rows.push(<FieldRenderer key={f.id} field={f} value={responses[f.id]} onChange={v => onChange(f.id, v)} />);
      i++;
    }
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {section.heading && (
        <div className="bg-teal-50 border-b border-teal-100 px-4 py-2">
          <h3 className="text-sm font-bold text-teal-800">{section.heading.label}</h3>
        </div>
      )}
      <div className="p-4 space-y-4">{rows}</div>
    </div>
  );
}

// Fields that are child-specific — cleared when copying responses to a new child
const isChildSpecificField = (field) => {
  if (field.type === "signature") return true;
  const label = field.label.toLowerCase();
  return (
    (label.includes("child") && (label.includes("name") || label.includes("first") || label.includes("last"))) ||
    (label.includes("student") && label.includes("name")) ||
    label.includes("date of birth") || label.includes("birth date") ||
    label.includes("dob") || label === "age" ||
    (label.includes("child") && label.includes("age")) ||
    label.includes("grade") || label.includes("classroom")
  );
};

// Stepper navigation bar
function StepperBar({ childNames, currentStep }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-5">
      {childNames.map((name, idx) => (
        <div key={idx} className="flex items-center gap-1 flex-shrink-0">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
            idx === currentStep
              ? "bg-teal-600 text-white"
              : idx < currentStep
              ? "bg-teal-100 text-teal-700"
              : "bg-slate-100 text-slate-400"
          }`}>
            {idx < currentStep && <CheckCircle2 className="h-3 w-3 flex-shrink-0" />}
            <span className="truncate max-w-[100px]">{name || `Child ${idx + 1}`}</span>
          </div>
          {idx < childNames.length - 1 && (
            <div className={`h-0.5 w-5 flex-shrink-0 rounded-full ${idx < currentStep ? "bg-teal-400" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ParentFormsPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null);
  const [lastSub, setLastSub] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // ── Multi-child session state ──
  const [sessionMode, setSessionMode] = useState(null); // null | 'setup' | 'filling'
  const [childNames, setChildNames] = useState([""]);
  const [currentStep, setCurrentStep] = useState(0);
  const [allResponses, setAllResponses] = useState({}); // { [stepIndex]: {fieldId: value} }
  const [draftIds, setDraftIds] = useState({});         // { [stepIndex]: draftId }
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [existingDrafts, setExistingDrafts] = useState([]);
  const [fromReview, setFromReview] = useState(false);
  const [sessionSubmitting, setSessionSubmitting] = useState(false);
  const [submitResults, setSubmitResults] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const navigate = useNavigate();

  // ── Health & Medical Records state ──
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});  // key: `${childId}-${docType}`
  const [uploadingFor, setUploadingFor] = useState(null);
  const [dragActiveFor, setDragActiveFor] = useState(null);

  const fetchForms = () => {
    setLoading(true);
    axios.get(`${API}/forms/parent/available`)
      .then(r => setForms(r.data.forms || []))
      .catch(() => toast.error("Unable to load forms"))
      .finally(() => setLoading(false));
  };

  const fetchDocuments = () => {
    setLoadingDocs(true);
    axios.get(`${API}/parent-communication/parent/documents`)
      .then(r => {
        const filtered = (r.data.documents || []).filter(
          d => HEALTH_DOC_TYPES.includes(d.docType)
        );
        setDocs(filtered);
      })
      .catch(() => {})
      .finally(() => setLoadingDocs(false));
  };

  useEffect(() => { fetchForms(); fetchDocuments(); }, []);

  // ── Health & Medical Records handlers ──
  const docKey = (childId, docType) => `${childId}-${docType}`;

  const handleDocFile = (childId, docType, file) => {
    const allowed = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Please upload a JPG, PNG, or PDF file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large. Maximum 10MB."); return; }
    setSelectedFiles(prev => ({ ...prev, [docKey(childId, docType)]: file }));
  };

  const handleDocUpload = async (childId, docType) => {
    const file = selectedFiles[docKey(childId, docType)];
    if (!file) return;
    const key = docKey(childId, docType);
    setUploadingFor(key);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(
        `${API}/parent-communication/parent/upload-document/${childId}/${docType}`,
        formData
      );
      toast.success("Document uploaded successfully!");
      setSelectedFiles(prev => ({ ...prev, [key]: null }));
      fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploadingFor(null);
    }
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    setDragActiveFor(key);
  };

  const handleDragLeave = () => setDragActiveFor(null);

  const handleDrop = (e, childId, docType) => {
    e.preventDefault();
    setDragActiveFor(null);
    const file = e.dataTransfer.files?.[0];
    if (file) handleDocFile(childId, docType, file);
  };

  const handleDeletePage = async (childId, docType, pageId) => {
    try {
      await axios.delete(`${API}/parent-communication/parent/document/${childId}/${docType}/page/${pageId}`);
      toast.success("Page removed");
      fetchDocuments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove page");
    }
  };

  const openPage = async (page) => {
    if (!page?.viewUrl) return;
    try {
      const res = await axios.get(page.viewUrl, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open file");
    }
  };

  const setField = (id, val) => setResponses(prev => ({ ...prev, [id]: val }));

  const openForm = async (form) => {
    setActiveForm(form);
    // Fetch previous submission for pre-fill
    try {
      const res = await axios.get(`${API}/forms/parent/last-submission/${form.id}`);
      const sub = res.data.submission;
      setLastSub(sub || null);
      if (sub?.responses) {
        const prefilled = {};
        for (const [k, v] of Object.entries(sub.responses)) {
          const fieldDef = (form.fields || []).find(f => f.id === k);
          if (fieldDef?.type === "signature") continue;
          if (fieldDef?.type === "date" && k === form.formDateFieldId) continue;
          prefilled[k] = v;
        }
        if (form.formDateFieldId === "custom" && form.customStartDate) {
          const dateField = (form.fields || []).find(f => f.type === "date" && !f.label.toLowerCase().includes("birth") && !f.label.toLowerCase().includes("dob"));
          if (dateField) prefilled[dateField.id] = form.customStartDate;
        }
        setResponses(prefilled);
      } else {
        const initial = {};
        if (form.formDateFieldId === "custom" && form.customStartDate) {
          const dateField = (form.fields || []).find(f => f.type === "date" && !f.label.toLowerCase().includes("birth") && !f.label.toLowerCase().includes("dob"));
          if (dateField) initial[dateField.id] = form.customStartDate;
        }
        setResponses(initial);
      }
    } catch {
      setLastSub(null);
      setResponses({});
    }
  };

  const submitForm = async () => {
    const missing = (activeForm.fields || []).filter(f =>
      f.required && !["section-heading","instruction"].includes(f.type) && !responses[f.id]
    );
    if (missing.length > 0) { toast.error(`Please complete: ${missing.map(f=>f.label).join(", ")}`); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/forms/parent/submit/${activeForm.id}`, { responses });
      if (res.data.dateWarning) {
        toast.warning(`Date Notice: ${res.data.dateWarning.message}`);
      } else {
        toast.success("Form submitted!");
      }
      fetchForms();
      setActiveForm(null); setLastSub(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to submit form");
    } finally { setSubmitting(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/forms/parent/submissions/${deleteTarget.submissionId}`);
      toast.success("Submission deleted");
      setDeleteTarget(null);
      fetchForms();
    } catch { toast.error("Unable to delete"); }
    finally { setDeleting(false); }
  };

  // ── Multi-child session helpers ──

  const openSessionSetup = async (form) => {
    setActiveForm(form);
    setSessionMode("setup");
    setChildNames([""]);
    setCurrentStep(0);
    setAllResponses({});
    setDraftIds({});
    setLastSavedAt(null);
    // Check for saved drafts from a previous session
    try {
      const res = await axios.get(`${API}/forms/parent/session/${form.id}`);
      setExistingDrafts(res.data.drafts || []);
    } catch {
      setExistingDrafts([]);
    }
  };

  const closeSession = () => {
    setActiveForm(null);
    setSessionMode(null);
    setChildNames([""]);
    setCurrentStep(0);
    setAllResponses({});
    setResponses({});
    setDraftIds({});
    setLastSavedAt(null);
    setExistingDrafts([]);
    setFromReview(false);
    setSessionSubmitting(false);
    setSubmitResults(null);
    setExpandedCard(null);
  };

  // Build the initial responses for a child at stepIndex
  // Step 0: same as single-child pre-fill (from lastSub)
  // Step 1+: copy step 0's responses, clear child-specific fields
  const buildResponsesForStep = (stepIndex, form, baseResponses) => {
    if (stepIndex === 0) return baseResponses;
    const step0 = allResponses[0] || {};
    const cleared = {};
    for (const [k, v] of Object.entries(step0)) {
      const fieldDef = (form.fields || []).find(f => f.id === k);
      if (fieldDef && isChildSpecificField(fieldDef)) continue;
      cleared[k] = v;
    }
    return cleared;
  };

  const beginSession = async () => {
    const names = childNames.map(n => n.trim()).filter(Boolean);
    if (names.length === 0) { toast.error("Enter at least one child's name"); return; }
    setChildNames(names);
    let prefilled = {};
    try {
      const res = await axios.get(`${API}/forms/parent/last-submission/${activeForm.id}`);
      const sub = res.data.submission;
      setLastSub(sub || null);
      if (sub?.responses) {
        for (const [k, v] of Object.entries(sub.responses)) {
          const fieldDef = (activeForm.fields || []).find(f => f.id === k);
          if (fieldDef?.type === "signature") continue;
          if (fieldDef?.type === "date" && k === activeForm.formDateFieldId) continue;
          prefilled[k] = v;
        }
      }
      if (activeForm.formDateFieldId === "custom" && activeForm.customStartDate) {
        const dateField = (activeForm.fields || []).find(f => f.type === "date" && !f.label.toLowerCase().includes("birth") && !f.label.toLowerCase().includes("dob"));
        if (dateField) prefilled[dateField.id] = activeForm.customStartDate;
      }
    } catch { setLastSub(null); }
    // Create server-side drafts for all children
    const ids = {};
    try {
      const res = await axios.post(`${API}/forms/parent/session/start`, {
        formTemplateId: activeForm.id,
        childNames: names,
      });
      (res.data.drafts || []).forEach((d, idx) => { ids[idx] = d.id; });
    } catch {
      toast.error("Unable to create drafts — your work will not be saved to server");
    }
    setDraftIds(ids);
    setResponses(prefilled);
    setAllResponses({ 0: prefilled });
    setCurrentStep(0);
    setSessionMode("filling");
  };

  // Save current step's draft to server
  const saveDraft = async (stepIdx, stepResponses) => {
    const draftId = draftIds[stepIdx];
    if (!draftId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/forms/parent/draft/${draftId}`, {
        responses: stepResponses,
        childName: childNames[stepIdx],
      });
      setLastSavedAt(new Date());
    } catch {
      toast.error("Unable to save — please try again");
    } finally {
      setSaving(false);
    }
  };

  // Load existing drafts and jump straight into filling
  const continueDrafts = () => {
    const names = existingDrafts.map(d => d.childName);
    const ids = {};
    const allR = {};
    existingDrafts.forEach((d, idx) => {
      ids[idx] = d.id;
      allR[idx] = d.responses || {};
    });
    setChildNames(names);
    setDraftIds(ids);
    setAllResponses(allR);
    setResponses(allR[0] || {});
    setCurrentStep(0);
    setLastSub(null);
    setSessionMode("filling");
  };

  const startFresh = async () => {
    try { await axios.delete(`${API}/forms/parent/session/${activeForm.id}`); } catch {}
    setExistingDrafts([]);
    setChildNames([""]);
  };

  // Navigate from filling to review (auto-saves current step)
  const goToReview = async () => {
    const saved = { ...allResponses, [currentStep]: responses };
    setAllResponses(saved);
    await saveDraft(currentStep, responses);
    setSessionMode("review");
    setFromReview(false);
    setExpandedCard(null);
  };

  // Edit a specific child from the review screen
  const editFromReview = (stepIdx) => {
    setFromReview(true);
    setResponses(allResponses[stepIdx] || {});
    setCurrentStep(stepIdx);
    setSessionMode("filling");
  };

  // Return to review from editing
  const returnToReview = async () => {
    const saved = { ...allResponses, [currentStep]: responses };
    setAllResponses(saved);
    await saveDraft(currentStep, responses);
    setFromReview(false);
    setSessionMode("review");
    setExpandedCard(null);
  };

  // Submit all drafts
  const submitAll = async () => {
    setSessionSubmitting(true);
    try {
      const res = await axios.post(`${API}/forms/parent/submit-all/${activeForm.id}`);
      setSubmitResults(res.data);
      setSessionMode("success");
      fetchForms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to submit — please try again");
    } finally {
      setSessionSubmitting(false);
    }
  };

  // Count completed required fields for a step
  const getFieldCompletion = (stepIdx) => {
    const r = allResponses[stepIdx] || {};
    const required = (activeForm?.fields || []).filter(f =>
      f.required && !["section-heading", "instruction"].includes(f.type)
    );
    const completed = required.filter(f => r[f.id]);
    return { completed: completed.length, total: required.length, missing: required.filter(f => !r[f.id]).map(f => f.label) };
  };

  const goToStep = async (targetStep) => {
    // Auto-save current step before navigating
    const saved = { ...allResponses, [currentStep]: responses };
    setAllResponses(saved);
    await saveDraft(currentStep, responses);
    // Load or build responses for target step
    const existing = saved[targetStep];
    const next = existing && Object.keys(existing).length > 0
      ? existing
      : buildResponsesForStep(targetStep, activeForm, saved);
    setResponses(next);
    setCurrentStep(targetStep);
  };

  // ── Session setup screen ──
  if (sessionMode === "setup" && activeForm) {
    return (
      <div className="min-h-screen bg-[#f6fbfa] px-4 py-8" data-testid="session-setup-screen">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={closeSession} className="text-slate-400 hover:text-slate-600 bg-white rounded-full p-2 shadow-sm">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{activeForm.title}</h1>
              <p className="text-sm text-slate-500">Multiple children enrollment</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-xl"><Users className="h-5 w-5 text-teal-600" /></div>
              <div>
                <p className="font-bold text-slate-900">How many children are you enrolling?</p>
                <p className="text-sm text-slate-500 mt-0.5">Enter each child's name. You'll fill out a separate form for each child, with your family's shared information pre-filled automatically.</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
              You can save your progress at any time. Hit the <strong>Save</strong> button and come back later — your work will be waiting.
            </div>

            {/* Continue where you left off */}
            {existingDrafts.length > 0 && (
              <div className="bg-teal-50 border border-teal-300 rounded-xl p-4 space-y-3">
                <p className="font-semibold text-teal-800 text-sm">Continue where you left off</p>
                <p className="text-xs text-teal-700">You have a saved session for {existingDrafts.length} child{existingDrafts.length !== 1 ? "ren" : ""}:</p>
                <div className="flex flex-wrap gap-2">
                  {existingDrafts.map((d, idx) => (
                    <span key={idx} className="text-xs bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full font-medium">{d.childName}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={continueDrafts} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm" data-testid="continue-drafts-btn">
                    Continue
                  </Button>
                  <Button variant="outline" onClick={startFresh} className="rounded-xl border-slate-300 text-slate-600 text-sm" data-testid="start-fresh-btn">
                    Start Fresh
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {childNames.map((name, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-400 w-6 text-right flex-shrink-0">{idx + 1}.</span>
                  <input
                    value={name}
                    onChange={e => {
                      const updated = [...childNames];
                      updated[idx] = e.target.value;
                      setChildNames(updated);
                    }}
                    placeholder={`Child ${idx + 1} full name`}
                    className="flex-1 border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    data-testid={`child-name-input-${idx}`}
                  />
                  {childNames.length > 1 && (
                    <button
                      onClick={() => setChildNames(childNames.filter((_, i) => i !== idx))}
                      className="text-slate-400 hover:text-red-500 p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => setChildNames([...childNames, ""])}
              className="flex items-center gap-2 text-sm text-teal-600 font-semibold hover:text-teal-800"
              data-testid="add-child-btn"
            >
              <Plus className="h-4 w-4" /> Add another child
            </button>

            <Button
              onClick={beginSession}
              disabled={!childNames.some(n => n.trim())}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-2xl py-5 text-base font-semibold"
              data-testid="begin-session-btn"
            >
              Begin Forms →
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Session stepper filling screen ──
  if (sessionMode === "filling" && activeForm) {
    const totalSteps = childNames.length;
    const isLastStep = currentStep === totalSteps - 1;

    return (
      <div className="min-h-screen bg-[#f6fbfa] px-4 py-8" data-testid="session-filling-screen">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <button onClick={closeSession} className="text-slate-400 hover:text-slate-600 bg-white rounded-full p-2 shadow-sm">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-slate-900 truncate">{activeForm.title}</h1>
              <p className="text-sm text-slate-500">Child {currentStep + 1} of {totalSteps}: <strong>{childNames[currentStep]}</strong></p>
            </div>
          </div>

          {/* Stepper bar */}
          <StepperBar childNames={childNames} currentStep={currentStep} />

          {/* Save instruction banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-2 text-xs text-blue-800">
            <span className="text-blue-500 flex-shrink-0">ℹ</span>
            <span>Hit <strong>Save</strong> to save your progress and return later. Your work is kept safe until you Submit All.</span>
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            {activeForm.headerText && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                {activeForm.headerText}
              </div>
            )}
            {(() => {
              const sorted = (activeForm.fields || []).sort((a, b) => a.order - b.order);
              const sections = groupFieldsIntoSections(sorted);
              const rendered = [];
              let i = 0;
              while (i < sections.length) {
                const s = sections[i];
                const next = sections[i + 1];
                if (isSideBySidePair(s, next)) {
                  rendered.push(
                    <div key={`pair-${i}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <SectionCard section={s} responses={responses} onChange={setField} />
                      <SectionCard section={next} responses={responses} onChange={setField} />
                    </div>
                  );
                  i += 2;
                } else if (s.heading) {
                  rendered.push(<SectionCard key={`s-${i}`} section={s} responses={responses} onChange={setField} />);
                  i += 1;
                } else {
                  rendered.push(
                    <div key={`bare-${i}`} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-4">
                      {s.fields.map(field => (
                        <FieldRenderer key={field.id} field={field} value={responses[field.id]} onChange={v => setField(field.id, v)} />
                      ))}
                    </div>
                  );
                  i += 1;
                }
              }
              return rendered;
            })()}
            {activeForm.footerText && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">
                {activeForm.footerText}
              </div>
            )}
          </div>

          {/* Navigation buttons */}
          <div className="mt-6 space-y-3">
            {/* Save button — wired to backend */}
            <Button
              variant="outline"
              className="w-full rounded-2xl border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => saveDraft(currentStep, responses)}
              disabled={saving || !draftIds[currentStep]}
              data-testid="session-save-btn"
            >
              {saving
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                : "Save Progress"}
            </Button>
            {lastSavedAt && (
              <p className="text-xs text-center text-slate-400">
                Last saved: {lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={() => goToStep(currentStep - 1)}
                  className="rounded-2xl"
                  data-testid="session-back-btn"
                >
                  ← Back
                </Button>
              )}
              <Button
                onClick={() => {
                  if (fromReview) { returnToReview(); }
                  else if (isLastStep) { goToReview(); }
                  else { goToStep(currentStep + 1); }
                }}
                className={`rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-semibold ${currentStep === 0 && !fromReview ? "col-span-2" : ""}`}
                data-testid="session-next-btn"
              >
                {fromReview
                  ? "Return to Review →"
                  : isLastStep
                  ? "Review All →"
                  : `Next: ${childNames[currentStep + 1] || `Child ${currentStep + 2}`} →`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Review All screen ──
  if (sessionMode === "review" && activeForm) {
    const allComplete = childNames.every((_, idx) => {
      const { completed, total } = getFieldCompletion(idx);
      return total === 0 || completed === total;
    });

    return (
      <div className="min-h-screen bg-[#f6fbfa] px-4 py-8" data-testid="session-review-screen">
        <div className="max-w-xl mx-auto space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => { setSessionMode("filling"); setCurrentStep(childNames.length - 1); setResponses(allResponses[childNames.length - 1] || {}); }}
              className="text-slate-400 hover:text-slate-600 bg-white rounded-full p-2 shadow-sm">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Review Before Submitting</h1>
              <p className="text-sm text-slate-500">{activeForm.title} · {childNames.length} child{childNames.length !== 1 ? "ren" : ""}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
            Review all forms below. Tap <strong>Edit</strong> on any child to make corrections. When everything looks right, tap <strong>Submit All</strong>.
          </div>

          {childNames.map((name, idx) => {
            const { completed, total, missing } = getFieldCompletion(idx);
            const isComplete = total === 0 || completed === total;
            const isExpanded = expandedCard === idx;
            const stepResponses = allResponses[idx] || {};

            return (
              <div key={idx} className={`bg-white rounded-2xl border-2 overflow-hidden ${isComplete ? "border-slate-200" : "border-amber-300"}`}
                data-testid={`review-card-${idx}`}>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isComplete ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`}>
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{name}</p>
                      {!isComplete
                        ? <p className="text-xs text-amber-600">{missing.length} required field{missing.length !== 1 ? "s" : ""} missing</p>
                        : <p className="text-xs text-teal-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Ready to submit</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => editFromReview(idx)}
                      className="text-xs text-blue-600 font-semibold hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-50"
                      data-testid={`edit-child-${idx}`}>
                      Edit
                    </button>
                    <button onClick={() => setExpandedCard(isExpanded ? null : idx)}
                      className="text-slate-400 hover:text-slate-600 p-1">
                      <svg className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-slate-100 px-4 py-3 space-y-1.5 bg-slate-50">
                    {(activeForm.fields || [])
                      .filter(f => !["section-heading", "instruction", "signature"].includes(f.type) && stepResponses[f.id])
                      .slice(0, 8)
                      .map(f => (
                        <div key={f.id} className="flex gap-2 text-xs">
                          <span className="text-slate-500 flex-shrink-0 w-32 truncate">{f.label}:</span>
                          <span className="text-slate-800 font-medium truncate">{String(stepResponses[f.id]).slice(0, 60)}</span>
                        </div>
                      ))}
                    {Object.keys(stepResponses).filter(k => stepResponses[k]).length === 0 && (
                      <p className="text-xs text-slate-400 italic">No responses entered yet</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {!allComplete && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
              Some required fields are missing. Tap <strong>Edit</strong> on the highlighted child to complete them before submitting.
            </div>
          )}

          <Button
            onClick={submitAll}
            disabled={sessionSubmitting}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-2xl py-5 text-base font-semibold mt-2"
            data-testid="submit-all-btn"
          >
            {sessionSubmitting
              ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Submitting...</>
              : `Submit All ${childNames.length} Form${childNames.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      </div>
    );
  }

  // ── Success screen ──
  if (sessionMode === "success" && submitResults) {
    return (
      <div className="min-h-screen bg-[#f6fbfa] px-4 py-8 flex items-center justify-center" data-testid="session-success-screen">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-5">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">All Forms Submitted!</h2>
            <p className="text-sm text-slate-500 mt-1">Your center has received {submitResults.submitted} form{submitResults.submitted !== 1 ? "s" : ""}.</p>
          </div>
          <div className="bg-teal-50 rounded-xl p-4 space-y-1.5">
            {(submitResults.results || []).map((r, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-teal-800">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium">{r.childName}</span>
              </div>
            ))}
          </div>
          <Button onClick={() => { closeSession(); fetchForms(); }}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-2xl py-4 font-semibold"
            data-testid="session-done-btn">
            Done
          </Button>
        </div>
      </div>
    );
  }

  // ── Active form filler (single-child — existing flow, untouched) ──
  if (activeForm) {
    const expStatus = lastSub ? computeExpStatus(lastSub) : null;
    const isRenewal = !!lastSub;
    const parentName = lastSub?.parentName || "";

    return (
      <div className="min-h-screen bg-[#f6fbfa] px-4 py-8" data-testid="parent-form-filler">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => { setActiveForm(null); setLastSub(null); setResponses({}); }}
              className="text-slate-400 hover:text-slate-600 bg-white rounded-full p-2 shadow-sm">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{activeForm.title}</h1>
              {activeForm.description && <p className="text-sm text-slate-500">{activeForm.description}</p>}
            </div>
          </div>

          {/* Re-sign banner */}
          {isRenewal && (expStatus === "expired" || expStatus === "expiring_soon") && (
            <div className={`rounded-xl border px-4 py-3 mb-4 flex items-start gap-2 ${expStatus==="expired"?"bg-red-50 border-red-200":"bg-amber-50 border-amber-200"}`}>
              <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${expStatus==="expired"?"text-red-500":"text-amber-500"}`} />
              <div>
                <p className={`text-sm font-semibold ${expStatus==="expired"?"text-red-800":"text-amber-800"}`}>
                  {expStatus==="expired" ? "Update & Re-sign Required" : "Expiring Soon — Please Update & Re-sign"}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Your previous answers are pre-filled. Review for any changes, enter today's date, re-sign, and resubmit.
                </p>
              </div>
            </div>
          )}
          {isRenewal && expStatus === "current" && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 mb-4 text-sm text-blue-700">
              Your previous answers are pre-filled. Update anything that has changed, re-sign, and resubmit.
            </div>
          )}

          {/* Form fields — smart section rendering */}
          <div className="space-y-4">
            {activeForm.headerText && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600 leading-relaxed whitespace-pre-wrap">
                {activeForm.headerText}
              </div>
            )}
            {(() => {
              const sorted = (activeForm.fields || []).sort((a, b) => a.order - b.order);
              const sections = groupFieldsIntoSections(sorted);
              const rendered = [];
              let i = 0;
              while (i < sections.length) {
                const s = sections[i];
                const next = sections[i + 1];
                if (isSideBySidePair(s, next)) {
                  rendered.push(
                    <div key={`pair-${i}`} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <SectionCard section={s} responses={responses} onChange={setField} />
                      <SectionCard section={next} responses={responses} onChange={setField} />
                    </div>
                  );
                  i += 2;
                } else if (s.heading) {
                  rendered.push(<SectionCard key={`s-${i}`} section={s} responses={responses} onChange={setField} />);
                  i += 1;
                } else {
                  // No heading — render fields directly (instructions, top-level fields)
                  rendered.push(
                    <div key={`bare-${i}`} className="bg-white rounded-2xl border border-slate-100 p-4 space-y-4">
                      {s.fields.map(field => (
                        <FieldRenderer key={field.id} field={field}
                          value={responses[field.id]} onChange={v => setField(field.id, v)} />
                      ))}
                    </div>
                  );
                  i += 1;
                }
              }
              return rendered;
            })()}
            {activeForm.footerText && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">
                {activeForm.footerText}
              </div>
            )}

            {/* Legal statement above submit */}
            <div className="bg-white rounded-2xl border border-slate-100 px-4 py-3">
              <p className="text-xs text-slate-500 leading-relaxed">
                By submitting this form, <strong>{parentName || "I"}</strong> certify that all information provided is accurate and complete, and that my electronic signature constitutes a legally binding signature under the ESIGN Act and applicable state law.
              </p>
            </div>
          </div>

          <Button onClick={submitForm} disabled={submitting}
            className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-semibold rounded-2xl"
            data-testid="submit-form-btn">
            {submitting ? <><Loader2 className="h-5 w-5 animate-spin mr-2"/>Submitting...</> : isRenewal ? "Update & Re-sign" : "Submit Form"}
          </Button>
        </div>
      </div>
    );
  }

  // ── Forms list ──
  return (
    <div className="min-h-screen bg-[#f6fbfa] px-4 py-8" data-testid="parent-forms-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/parent")} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="h-5 w-5"/>
          </button>
          <h1 className="text-xl font-bold text-slate-900">Enrollment Forms (Custom Digital)</h1>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto"/></div>
        ) : forms.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30"/>
            <p className="font-medium">No forms available yet</p>
            <p className="text-sm">Your center will publish forms here when ready</p>
          </div>
        ) : (
          <div className="space-y-3">
            {forms.map(form => {
              const expStatus = form.submissionStatus !== "not_started" && form.latestSub
                ? computeExpStatus(form.latestSub)
                : form.submissionStatus === "submitted" ? "current" : "not_started";
              const s = EXP_STATUS[expStatus] || EXP_STATUS.not_started;
              const daysLeft = form.latestSub?.expiresAt ? daysUntil(form.latestSub.expiresAt) : null;
              const canOpen = expStatus !== "not_started"
                ? true  // always allow update/re-sign
                : true; // not started — open to fill

              return (
                <div key={form.id} className="rounded-2xl border bg-white shadow-sm" data-testid={`parent-form-${form.id}`}>
                  <button className={`w-full text-left p-4 ${canOpen?"hover:bg-teal-50/30 transition":""}`}
                    onClick={() => openForm(form)}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{form.title}</p>
                        {form.description && <p className="text-xs text-slate-500 mt-0.5">{form.description}</p>}
                        <p className="text-xs text-slate-400 mt-1">{(form.fields||[]).filter(f=>!["section-heading","instruction"].includes(f.type)).length} fields</p>
                      </div>
                      <Badge className={`text-[11px] flex-shrink-0 ${s.cls}`}>{s.label}</Badge>
                    </div>
                    {expStatus === "current" && daysLeft !== null && (
                      <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3"/> Submitted · Expires {new Date(form.latestSub.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                    {expStatus === "expiring_soon" && daysLeft !== null && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <Clock className="h-3 w-3"/> Expires in {daysLeft} day{daysLeft!==1?"s":""}
                        {" "}· Tap to Update & Re-sign
                      </p>
                    )}
                    {expStatus === "expired" && (
                      <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3"/> Expired · Update & Re-sign Required
                      </p>
                    )}
                  </button>

                  {/* Delete button — only for submitted forms */}
                  {form.submissionStatus !== "not_started" && form.latestSub?.id && (
                    <div className="border-t border-slate-100 px-4 py-2 flex justify-between items-center">
                      <button
                        onClick={() => openSessionSetup(form)}
                        className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1 font-semibold"
                        data-testid={`multi-child-btn-${form.id}`}
                      >
                        <Users className="h-3 w-3" /> Enroll multiple children
                      </button>
                      <button
                        onClick={() => setDeleteTarget({ formTitle: form.title, submissionId: form.latestSub.id, orgName: "" })}
                        className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1"
                        data-testid={`delete-submission-${form.id}`}>
                        <Trash2 className="h-3 w-3"/> Delete submission
                      </button>
                    </div>
                  )}
                  {/* Multi-child button for forms not yet started */}
                  {form.submissionStatus === "not_started" && (
                    <div className="border-t border-slate-100 px-4 py-2">
                      <button
                        onClick={() => openSessionSetup(form)}
                        className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1 font-semibold"
                        data-testid={`multi-child-btn-${form.id}`}
                      >
                        <Users className="h-3 w-3" /> Enroll multiple children
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Health & Medical Records ── */}
        {docs.length > 0 && (() => {
          // Group docs by childId
          const children = {};
          docs.forEach(d => {
            if (!children[d.childId]) children[d.childId] = { name: d.childName, docs: {} };
            children[d.childId].docs[d.docType] = d;
          });

          return (
            <div className="mt-8" data-testid="health-records-section">
              {/* Section divider */}
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-slate-200"/>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">Health &amp; Medical Records</span>
                <div className="flex-1 h-px bg-slate-200"/>
              </div>

              <div className="space-y-6">
                {Object.entries(children).map(([childId, child]) => (
                  <div key={childId}>
                    {/* Child name label — only shown when parent has multiple children */}
                    {Object.keys(children).length > 1 && (
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 px-1">{child.name}</p>
                    )}

                    <div className="space-y-3">
                      {HEALTH_DOC_TYPES.map(docType => {
                        const doc = child.docs[docType];
                        if (!doc) return null;
                        // Parent UI only: if any pages are uploaded, show "On File" regardless of staff review status.
                        // Compliance scoring and staff dashboard are unaffected — they read backend status separately.
                        const displayStatus = (doc.pageCount > 0 && doc.status === "needs_upload") ? "current" : doc.status;
                        const st = DOC_STATUS[displayStatus] || DOC_STATUS.needs_upload;
                        const key = docKey(childId, docType);
                        const file = selectedFiles[key];
                        const isUploading = uploadingFor === key;
                        const isDragging = dragActiveFor === key;

                        return (
                          <div key={docType}
                            className="rounded-2xl border bg-white shadow-sm"
                            data-testid={`health-doc-${childId}-${docType}`}>

                            {/* Header row — matches enrollment form card exactly */}
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                  <p className="font-semibold text-slate-900">{HEALTH_DOC_LABELS[docType]}</p>
                                  <p className="text-xs text-slate-400 mt-0.5">Yearly</p>
                                </div>
                                <Badge className={`text-[11px] flex-shrink-0 ${st.cls}`}>{st.label}</Badge>
                              </div>

                              {/* Expiry info */}
                              {doc.status === "current" && doc.expirationDate && (
                                <p className="text-xs text-emerald-600 flex items-center gap-1 mb-3">
                                  <CheckCircle2 className="h-3 w-3"/> On File · Expires {new Date(doc.expirationDate).toLocaleDateString()}
                                </p>
                              )}
                              {doc.status === "expires_soon" && doc.expirationDate && (
                                <p className="text-xs text-amber-600 flex items-center gap-1 mb-3">
                                  <Clock className="h-3 w-3"/> Expires {new Date(doc.expirationDate).toLocaleDateString()} · Tap to update
                                </p>
                              )}
                              {doc.status === "expired" && doc.expirationDate && (
                                <p className="text-xs text-red-600 flex items-center gap-1 mb-3">
                                  <AlertTriangle className="h-3 w-3"/> Expired {new Date(doc.expirationDate).toLocaleDateString()} · Update required
                                </p>
                              )}

                              {/* Drag & Drop upload zone */}
                              <label
                                htmlFor={`file-input-${key}`}
                                className={`block rounded-xl border-2 border-dashed transition-all cursor-pointer
                                  ${isDragging
                                    ? "border-teal-400 bg-teal-50"
                                    : file
                                    ? "border-emerald-300 bg-emerald-50"
                                    : "border-slate-200 bg-slate-50 hover:border-teal-300 hover:bg-teal-50/40"}`}
                                onDragOver={e => handleDragOver(e, key)}
                                onDragLeave={handleDragLeave}
                                onDrop={e => handleDrop(e, childId, docType)}
                                data-testid={`drop-zone-${key}`}
                              >
                                <div className="flex flex-col items-center justify-center py-5 px-4 text-center">
                                  {file ? (
                                    <>
                                      <CheckCircle className="h-6 w-6 text-emerald-500 mb-1.5"/>
                                      <p className="text-xs font-semibold text-emerald-700 truncate max-w-[200px]">{file.name}</p>
                                      <p className="text-xs text-slate-400 mt-0.5">{(file.size / 1024).toFixed(0)} KB · Ready to upload</p>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="h-6 w-6 text-slate-300 mb-1.5"/>
                                      <p className="text-xs font-semibold text-slate-500">Drop file here or tap to browse</p>
                                      <p className="text-xs text-slate-400 mt-0.5">JPG · PNG · PDF &nbsp;·&nbsp; Max 10MB</p>
                                    </>
                                  )}
                                </div>
                                <input
                                  id={`file-input-${key}`}
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.pdf"
                                  className="hidden"
                                  onChange={e => { const f = e.target.files?.[0]; if (f) handleDocFile(childId, docType, f); }}
                                  data-testid={`file-input-${key}`}
                                />
                              </label>
                            </div>

                            {/* Uploaded pages list — multi-page support */}
                            {doc.pageCount > 0 && (
                              <div className="border-t border-slate-100 px-4 py-2.5 space-y-1.5" data-testid={`pages-list-${key}`}>
                                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                  {doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'} uploaded
                                </p>
                                {doc.pages.map((p, idx) => (
                                  <div key={p.id} className="flex items-center gap-2 text-xs" data-testid={`page-row-${p.id}`}>
                                    <FileText className="h-3 w-3 text-slate-400 flex-shrink-0"/>
                                    <button onClick={() => openPage(p)} className="flex-1 text-left text-slate-700 hover:text-teal-600 truncate underline-offset-2 hover:underline" title={p.filename}>
                                      Page {idx + 1} · {p.filename}
                                    </button>
                                    <button onClick={() => handleDeletePage(childId, docType, p.id)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`delete-page-${p.id}`} title="Remove page">
                                      <X className="h-3.5 w-3.5"/>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Upload action row — only shown when file is selected */}
                            {file && (
                              <div className="border-t border-slate-100 px-4 py-2 flex justify-between items-center">
                                <button
                                  onClick={() => setSelectedFiles(prev => ({ ...prev, [key]: null }))}
                                  className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1"
                                  data-testid={`clear-file-${key}`}
                                >
                                  <X className="h-3 w-3"/> Clear
                                </button>
                                <Button
                                  onClick={() => handleDocUpload(childId, docType)}
                                  disabled={isUploading}
                                  className="rounded-full bg-teal-600 hover:bg-teal-700 text-white text-xs px-4 py-1.5 h-auto"
                                  data-testid={`upload-btn-${key}`}
                                >
                                  {isUploading
                                    ? <><Loader2 className="h-3 w-3 animate-spin mr-1.5"/>Uploading…</>
                                    : <><Upload className="h-3 w-3 mr-1.5"/>Upload</>}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" data-testid="delete-confirm-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="h-5 w-5 text-red-500"/>
              </div>
              <div>
                <p className="font-bold text-slate-900">Permanently delete this submission?</p>
                <p className="text-sm text-slate-600 mt-1">
                  <strong>{deleteTarget.formTitle}</strong> will be removed from your portal and cannot be undone.
                </p>
              </div>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 leading-relaxed">
              <p className="font-semibold mb-1">How to get this form back</p>
              If you need to resubmit this form later, open the Enrollment Forms section — the form will still be available as long as your center has it published. Simply fill it out again and resubmit. If the form no longer appears, contact your center director and ask them to re-publish it.
            </div>

            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button onClick={confirmDelete} disabled={deleting}
                className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin"/> : "Yes, Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
